<script setup lang="ts">
import {ref, computed, onMounted, onBeforeUnmount, nextTick} from 'vue';
import {GET} from '../modules/fetch.ts';
import {isAppleDevice} from '../domutils.ts';
import {setupViewBoxPanZoom, type ViewBoxPanZoomHandle} from './allspice_render/viewbox_panzoom.ts';
import {gerberUnit, isNegativeImage, isNonGerberRender, type Box} from './gerber-render.ts';

const props = defineProps<{
  rawUrl: string;
  fileName: string;
  // When set, render this (base) revision against the head revision as a visual
  // diff: unchanged copper keeps the original color, added copper is green,
  // removed copper is red. Omitted for single-file view and added files.
  baseUrl?: string;
}>();

const LAYER_LABELS: Record<string, string> = {
  tcu: 'Top Copper',
  bcu: 'Bottom Copper',
  icu: 'Inner Copper',
  tsm: 'Top Soldermask',
  bsm: 'Bottom Soldermask',
  tsp: 'Top Solderpaste',
  bsp: 'Bottom Solderpaste',
  tss: 'Top Silkscreen',
  bss: 'Bottom Silkscreen',
  out: 'Board Outline',
  drl: 'Drill',
  mil: 'Mill',
};

// Diff colors. Added/removed match crysknife's standard PCB diff shades
// (DIFF_GREEN / DIFF_RED in crysknife_lib color.rs); unchanged keeps the normal
// gerber color.
const COLOR_UNCHANGED = '#cfd6e4';
const COLOR_ADDED = '#259870';
const COLOR_REMOVED = '#db454e';
// Fade unchanged copper so added/removed stand out, like the PCB diff backdrop.
const UNCHANGED_OPACITY = 0.35;

type Status = 'idle' | 'loading' | 'ready' | 'error';

const status = ref<Status>('idle');
const errorMessage = ref<string>('');
const svgMarkup = ref<string>('');
const layerType = ref<string | null>(null);
const showHint = ref(true);
const cursor = ref<{x: number; y: number} | null>(null);
const unit = ref<'mm' | 'in'>('mm'); // from the gerber's %MO; drives the readout label
const hideUnchanged = ref(false);

const region = ref<HTMLDivElement | null>(null); // .selectable-region, sized by the controller
const panElem = ref<HTMLDivElement | null>(null); // receives pointer/wheel events; holds the SVG
let panZoom: ViewBoxPanZoomHandle | null = null;
let flipY = 0; // gerber-to-svg content transform: user_y = flipY - gerber_y (SVG user units)
// Per-instance suffix so SVG element ids (and gerber-to-svg's internal aperture
// ids) don't collide when several gerber viewers mount on one page.
const uid = Math.random().toString(36).slice(2, 9);

const isDiff = computed(() => Boolean(props.baseUrl));

const layerLabel = computed(() => {
  if (!layerType.value) return null;
  return LAYER_LABELS[layerType.value] ?? layerType.value;
});

const zoomHint = computed(() => `${isAppleDevice() ? 'Shift' : 'Ctrl'} + Scroll to zoom`);

async function fetchText(url: string): Promise<string> {
  const resp = await GET(url);
  if (!resp.ok) {
    throw new Error(`Failed to fetch file (${resp.status} ${resp.statusText})`);
  }
  return resp.text();
}

async function toSvg(gerberText: string, id: string): Promise<string> {
  // Lazy-loaded so the tracespace bundle + Node polyfills code-split into their
  // own chunk and don't bloat the main entry.
  // ponytail: crysknife seam — for Phase 2, replace this client-side render with
  // a call to a crysknife diff render; the template/props/mount stay the same.
  if (isNegativeImage(gerberText)) {
    throw new Error("This layer is a negative film (%IPNEG), which the viewer can't render yet.");
  }
  const {default: gerberToSvg} = await import('gerber-to-svg');
  return await new Promise<string>((resolve, reject) => {
    // Distinct ids namespace each revision's internal aperture ids so base and
    // head geometry don't collide when composed into one SVG.
    const converter = gerberToSvg(gerberText, {id}, (err: Error | null, result: string) => {
      if (err) reject(err);
      else if (isNonGerberRender(converter.viewBox, converter.parser.format.places !== null)) reject(new Error('No Gerber artwork found'));
      else resolve(result);
    });
  });
}

async function loadGerber(url: string, id: string): Promise<{text: string; svg: string}> {
  const text = await fetchText(url);
  return {text, svg: await toSvg(text, id)};
}

function parseSvg(markup: string): SVGSVGElement {
  const doc = new DOMParser().parseFromString(markup, 'image/svg+xml');
  return doc.querySelector('svg') as SVGSVGElement;
}

function viewBoxOf(svg: SVGSVGElement): Box {
  const [x, y, w, h] = (svg.getAttribute('viewBox') ?? '0 0 0 0').split(/\s+/).map(Number);
  return {x, y, w, h};
}

// gerber-to-svg wraps content in translate(0,H) scale(1,-1): user_y = H - gerber_y.
// Grab H so the cursor readout can report real board (gerber) coordinates.
function flipYFrom(markup: string): number {
  const m = markup.match(/translate\(\s*0\s*,\s*(-?[\d.]+)\s*\)\s*scale\(\s*1\s*,\s*-1\s*\)/);
  return m ? Number(m[1]) : 0;
}

// gerber-to-svg fills with currentColor, so the same geometry can be reused via
// <use> at different colors. Compose one SVG in user space (so it pans/zooms
// natively): unchanged = head∩base in the original color, removed = base∖head in
// red, added = head∖base in green. Masks give the set differences: a "not X" mask
// is white everywhere with X's copper painted black, so it shows only where X is
// absent; "showBase" paints base copper white to show only where base is present.
function composeDiffSvg(baseMarkup: string, headMarkup: string): string {
  const baseSvg = parseSvg(baseMarkup);
  const headSvg = parseSvg(headMarkup);
  const b = viewBoxOf(baseSvg);
  const h = viewBoxOf(headSvg);
  // gerber-to-svg flips Y with a per-file offset H (translate(0,H) scale(1,-1)).
  // If the two revisions have different Y bounds, their H differs, so identical
  // copper would be vertically offset and the masks would mark it as changed.
  // Shift the base into the head's coordinate space by the H delta (applied to
  // the base group below and to its viewBox here) so the two align exactly.
  const dyBase = flipYFrom(headMarkup) - flipYFrom(baseMarkup);
  const bY = b.y + dyBase;
  const minX = Math.min(b.x, h.x);
  const minY = Math.min(bY, h.y);
  const w = Math.max(b.x + b.w, h.x + h.w) - minX;
  const hh = Math.max(bY + b.h, h.y + h.h) - minY;
  const vb = `${minX} ${minY} ${w} ${hh}`;
  const rgn = `x="${minX}" y="${minY}" width="${w}" height="${hh}"`;
  // Namespace ids per instance so url(#…) refs resolve within this SVG, not the
  // first gerber viewer on the page.
  const base = `dg-base-${uid}`;
  const head = `dg-head-${uid}`;
  const bg = `dg-bg-${uid}`;
  const showBase = `dg-showBase-${uid}`;
  const notBase = `dg-notBase-${uid}`;
  const notHead = `dg-notHead-${uid}`;

  return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${vb}" width="100%" height="100%" preserveAspectRatio="xMidYMid meet">
    <defs>
      <g id="${base}" transform="translate(0 ${dyBase})">${baseSvg.innerHTML}</g>
      <g id="${head}">${headSvg.innerHTML}</g>
      <rect id="${bg}" ${rgn} fill="#fff"/>
      <mask id="${showBase}" maskUnits="userSpaceOnUse" ${rgn}>
        <use href="#${base}" style="color:#fff"/>
      </mask>
      <mask id="${notBase}" maskUnits="userSpaceOnUse" ${rgn}>
        <use href="#${bg}"/><use href="#${base}" style="color:#000"/>
      </mask>
      <mask id="${notHead}" maskUnits="userSpaceOnUse" ${rgn}>
        <use href="#${bg}"/><use href="#${head}" style="color:#000"/>
      </mask>
    </defs>
    <use class="gerber-unchanged" href="#${head}" style="color:${COLOR_UNCHANGED}" opacity="${UNCHANGED_OPACITY}" mask="url(#${showBase})"/>
    <use href="#${base}" style="color:${COLOR_REMOVED}" mask="url(#${notHead})"/>
    <use href="#${head}" style="color:${COLOR_ADDED}" mask="url(#${notBase})"/>
  </svg>`;
}

async function classifyLayer() {
  try {
    const {default: whatsThatGerber} = await import('whats-that-gerber');
    const baseName = props.fileName.split('/').pop() ?? props.fileName;
    layerType.value = whatsThatGerber([baseName])[baseName]?.type ?? null;
  } catch {
    layerType.value = null;
  }
}

async function fetchAndRender() {
  status.value = 'loading';
  errorMessage.value = '';
  try {
    classifyLayer();
    if (isDiff.value) {
      const [head, base] = await Promise.all([
        loadGerber(props.rawUrl, `head-${uid}`),
        loadGerber(props.baseUrl as string, `base-${uid}`),
      ]);
      flipY = flipYFrom(head.svg);
      unit.value = gerberUnit(head.text);
      svgMarkup.value = composeDiffSvg(base.svg, head.svg);
    } else {
      const head = await loadGerber(props.rawUrl, `gerber-${uid}`);
      flipY = flipYFrom(head.svg);
      unit.value = gerberUnit(head.text);
      svgMarkup.value = head.svg;
    }
    status.value = 'ready';
    await nextTick();
    setupView();
  } catch (err) {
    errorMessage.value = err instanceof Error ? err.message : String(err);
    status.value = 'error';
  }
}

function setupView() {
  if (!region.value || !panElem.value) return;
  const svg = panElem.value.querySelector('svg') as SVGSVGElement | null;
  if (!svg) return;
  // gerber-to-svg emits real-world width/height (e.g. "30mm"), which renders
  // tiny. Strip those and let viewBox + preserveAspectRatio drive layout. (The
  // composed diff SVG already sets these; the single-file SVG needs them.)
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.setAttribute('width', '100%');
  svg.setAttribute('height', '100%');
  svg.setAttribute('preserveAspectRatio', 'xMidYMid meet');

  // Reuse the schematic/PCB viewer's pan/zoom (drag to pan, modifier+wheel and
  // double-click to zoom, crisp viewBox zoom). No inspector here, so
  // inspectorTarget is just the element.
  panZoom = setupViewBoxPanZoom({
    elem: panElem.value,
    region: region.value,
    svg,
    inspectorTarget: panElem.value,
    onScrollZoom: () => {
      showHint.value = false;
    },
  });
}

// Report the board (gerber) coordinate under the cursor.
function onPointerMove(e: PointerEvent) {
  const svg = panElem.value?.querySelector('svg') as SVGSVGElement | null;
  const ctm = svg?.getScreenCTM();
  if (!svg || !ctm) return;
  const p = new DOMPoint(e.clientX, e.clientY).matrixTransform(ctm.inverse());
  // SVG user space is file units × 1000, so ÷1000 gives the file's own unit.
  cursor.value = {x: p.x / 1000, y: (flipY - p.y) / 1000};
}

function onPointerLeave() {
  cursor.value = null;
}

function teardownView() {
  panZoom?.destroy();
  panZoom = null;
}

function viewSource() {
  const url = new URL(window.location.href);
  url.searchParams.set('display', 'source');
  window.location.assign(url.toString());
}

onMounted(fetchAndRender);
onBeforeUnmount(teardownView);
</script>

<template>
  <div
    ref="region"
    class="gerber-viewport tw:relative tw:w-full tw:bg-black tw:overflow-hidden"
  >
    <div v-if="status === 'loading'" class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:text-secondary">
      <div class="is-loading tw:size-8 tw:mr-3"></div>
      <span>Rendering Gerber…</span>
    </div>

    <div v-else-if="status === 'error'" class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:p-4">
      <div class="ui error message">
        <div class="header">Could not render this file as a Gerber</div>
        <p>{{ errorMessage }}</p>
        <div class="tw:mt-2">
          <button class="ui mini button" @click="viewSource">View raw text</button>
          <a class="ui mini button" :href="rawUrl">Download</a>
        </div>
      </div>
    </div>

    <!-- eslint-disable vue/no-v-html -->
    <div
      v-show="status === 'ready'"
      ref="panElem"
      class="gerber-canvas"
      :class="{'hide-unchanged': hideUnchanged}"
      v-html="svgMarkup"
      @pointermove="onPointerMove"
      @pointerleave="onPointerLeave"
    ></div>
    <!-- eslint-enable vue/no-v-html -->

    <div v-if="status === 'ready' && layerLabel" class="gerber-badge tw:absolute tw:top-2 tw:left-2 tw:px-2 tw:py-1 tw:text-xs tw:uppercase tw:tracking-wide tw:rounded">
      {{ layerLabel }}
    </div>

    <div v-if="status === 'ready' && isDiff" class="gerber-nav tw:absolute tw:top-2 tw:right-2 tw:flex tw:items-center tw:gap-2 tw:px-2 tw:py-1 tw:text-xs tw:rounded">
      <button class="gerber-nav-btn gerber-nav-toggle" :aria-pressed="hideUnchanged" @click="hideUnchanged = !hideUnchanged">Hide unchanged</button>
    </div>

    <div v-if="status === 'ready' && cursor" class="gerber-coord tw:absolute tw:bottom-2 tw:left-2 tw:px-2 tw:py-1 tw:text-xs tw:rounded tw:tabular-nums tw:pointer-events-none">
      X {{ cursor.x.toFixed(unit === 'in' ? 4 : 2) }} {{ unit }} &nbsp; Y {{ cursor.y.toFixed(unit === 'in' ? 4 : 2) }} {{ unit }}
    </div>

    <div v-if="status === 'ready' && showHint" class="gerber-hint tw:absolute tw:bottom-2 tw:right-2 tw:px-2 tw:py-1 tw:text-xs tw:rounded tw:pointer-events-none">
      {{ zoomHint }}
    </div>
  </div>
</template>

<style scoped>
.gerber-viewport {
  /* Fixed render colors: the viewport is always dark, independent of the theme. */
  --gerber-fg: #cfd6e4;
  --gerber-fg-strong: #fff;
  --gerber-overlay-bg: rgb(0 0 0 / 55%);
  position: relative;
  max-height: min(78vh, 900px);
  min-height: 360px;
  cursor: default;
  user-select: none;
}
.gerber-canvas {
  position: absolute;
  inset: 0;
}
/* stylelint-disable selector-pseudo-class-no-unknown */
.gerber-canvas :deep(svg) {
  display: block;
  /* Single-file view fills with currentColor; the diff SVG sets its own colors. */
  color: var(--gerber-fg);
}
/* stylelint-enable selector-pseudo-class-no-unknown */
.gerber-badge,
.gerber-hint,
.gerber-coord,
.gerber-nav {
  background: var(--gerber-overlay-bg);
  color: var(--gerber-fg);
}
.gerber-nav-btn {
  background: none;
  border: none;
  color: var(--gerber-fg);
  cursor: pointer;
  font-size: 1.1em;
  line-height: 1;
  padding: 0 0.2em;
}
.gerber-nav-btn:hover,
.gerber-nav-toggle[aria-pressed="true"] {
  color: var(--gerber-fg-strong);
}
.gerber-nav-toggle {
  font-size: 1em;
}
.gerber-nav-toggle[aria-pressed="true"] {
  text-decoration: underline;
}
/* stylelint-disable selector-pseudo-class-no-unknown */
.gerber-canvas.hide-unchanged :deep(.gerber-unchanged) {
  display: none;
}
/* stylelint-enable selector-pseudo-class-no-unknown */
</style>
